import { Component, effect, Injector, input, Input, OnInit, signal, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Employee } from '@app/models/employee/employee.model';
import { HumiInquiryStatus, KYC_KYB_URLS } from '@app/models/kyc-kyb/types';
import { PlatformModule } from '@app/platform.module';
import { KYCKYBService } from '@app/services/kyc-kyb/kyc-kyb.service';
import { TranslateModule } from '@ngx-translate/core';
import { KYCKYBHeader } from '../kyc-kyb-header/kyc-kyb-header.component';
import { type PersonaScope } from '../types';

@Component({
    templateUrl: 'kyc-kyb-status.template.html',
    selector: 'app-kyc-kyb-status',
    standalone: true,
    imports: [TranslateModule, KYCKYBHeader, PlatformModule],
    styleUrls: ['./kyc-kyb-status.style.scss'],
})
export class KYCKYBStatus implements OnInit {
    @Input({ required: true }) scope!: PersonaScope;
    @Input() employee?: Employee;
    headingLevel = input<'1' | '2' | '3'>('2');

    status: Signal<HumiInquiryStatus | undefined> = signal(undefined);

    /**
     * If the user's KYC/KYB status is one that requires 3rd party intervention (ie. approving or declining an inquiry), we will re-check the status from the BE
     * Otherwise, we will just serve the cached status from the last time the status was retrieved
     */
    refresh = effect(() => {
        if (this.status() && !this.employee) {
            this.knowYourService.checkForRefresh(this.scope);
        }
        // Always destroy the effect after running once. That way it only runs once per render
        this.refresh.destroy();
    });

    constructor(
        private knowYourService: KYCKYBService,
        private injector: Injector
    ) {}

    get linkToPreRedirect(): string {
        return KYC_KYB_URLS[this.scope];
    }

    ngOnInit(): void {
        if (this.scope === 'KYC') {
            this.status = this.employee
                ? toSignal(this.knowYourService.getAccountStatusForEmployee(this.employee), { injector: this.injector }) // Gets the supplied employee's status
                : toSignal(this.knowYourService.accountStatus, { injector: this.injector }); // Gets the user's own status
        } else {
            this.status = toSignal(this.knowYourService.companyStatus, { injector: this.injector });
        }
    }
}
